/**
 * Service desk settings — Step 95.
 *
 * Mined from Kai's `Service Desk Settings.html`. Two blocks, as the reference
 * has them — General and Security — and one control that is not cosmetic:
 * **Next ticket ID**, the number the Freshdesk import needs seeded above the
 * highest legacy number so years of emailed references keep resolving.
 *
 * The date and time previews are computed by the SAME shared function the
 * app formats with, so what the admin sees is what agents get.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, Settings2, ShieldCheck, TextCursorInput } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { TokenSelect } from '../../components/ui/tokenselect';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import {
  DEFAULT_REPLY_TEMPLATE,
  REPLY_TOKENS,
  formatSample,
  hasCaretGap,
  type ServiceDeskSettings,
} from '@itsm/shared';

interface Payload {
  settings: ServiceDeskSettings;
  sample: string;
  nextTicketId: number;
  highestTicketNumber: number;
  codeUploadAccept: string[];
  timeZones: string[];
}

const TYPE_LABELS: Record<string, string> = {
  'image/jpeg': 'JPEG image',
  'image/png': 'PNG image',
  'image/webp': 'WebP image',
  'image/gif': 'GIF image',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (.pptx)',
};

export function ServiceDeskPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ServiceDeskSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loaded = useQuery({
    queryKey: qk.serviceDesk,
    queryFn: () => apiFetch<Payload>('/api/admin/service-desk'),
  });

  const stored = loaded.data?.settings ?? null;
  const current = draft ?? stored;
  const dirty = !!stored && !!current && JSON.stringify(current) !== JSON.stringify(stored);
  useDirty(dirty);

  const save = useMutation({
    mutationFn: () => apiSend('/api/admin/service-desk', 'PUT', { settings: current }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'Those settings could not be saved.');
      setError(null);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: qk.serviceDesk });
      toast.success('Saved.');
    },
  });

  if (loaded.isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm font-medium">Settings could not be loaded.</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }
  if (loaded.isPending || !current) {
    return (
      <div className="max-w-3xl p-6">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  const d = loaded.data;
  const set = (patch: Partial<ServiceDeskSettings>) => setDraft({ ...current, ...patch });
  /** Step 179 — whether this template says where typing starts, or leaves
   *  the caret to fall off the end. */
  const startsAtGap = hasCaretGap(current.replyTemplate || DEFAULT_REPLY_TEMPLATE);
  const setSec = (patch: Partial<ServiceDeskSettings['security']>) =>
    setDraft({ ...current, security: { ...current.security, ...patch } });
  // The narrowed list, as a set of ticks over the code's list. `null` = all.
  const allowed = new Set(current.security.uploadTypes ?? d.codeUploadAccept);

  return (
    <div className="max-w-3xl p-6">
      <h1 className="flex items-center gap-2 text-lg font-semibold">
        <Settings2 className="h-5 w-5" /> Service desk settings
      </h1>
      <p className="mt-0.5 text-sm text-muted-foreground">Formats, numbering and who may reach the desk.</p>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {/* ── General ───────────────────────────────────────────────────── */}
      <section className="mt-6 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold">General</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Language</label>
            <Select value={current.language} onChange={(e) => set({ language: e.target.value })}>
              <option value="en-GB">English (UK)</option>
              <option value="en-US">English (US)</option>
              <option value="en-IE">English (Ireland)</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Time zone</label>
            <Select value={current.timeZone} onChange={(e) => set({ timeZone: e.target.value })}>
              {d.timeZones.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Date format</label>
            <Select
              value={current.dateFormat}
              onChange={(e) => set({ dateFormat: e.target.value as ServiceDeskSettings['dateFormat'] })}
            >
              <option value="dmy">Day-Month-Year</option>
              <option value="mdy">Month-Day-Year</option>
              <option value="ymd">Year-Month-Day</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Time format</label>
            <Select
              value={current.timeFormat}
              onChange={(e) => set({ timeFormat: e.target.value as ServiceDeskSettings['timeFormat'] })}
            >
              <option value="24h">24-hour</option>
              <option value="12h">12-hour</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Currency</label>
            <Input
              value={current.currency}
              maxLength={3}
              className="font-mono uppercase"
              onChange={(e) => set({ currency: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="flex items-end">
            <p className="text-sm text-muted-foreground">
              Looks like <span className="font-medium text-foreground">{formatSample(current)}</span>
            </p>
          </div>
        </div>
        <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input"
            checked={current.showTrashedInReports}
            onChange={(e) => set({ showTrashedInReports: e.target.checked })}
          />
          <span>
            <span className="font-medium">Include binned, spam and merged tickets in reports</span>
            <span className="block text-xs text-muted-foreground">
              Changes every count, now and historically.
            </span>
          </span>
        </label>
      </section>

      {/* ── How replies open — Step 177 ──────────────────────────────── */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-1 text-sm font-semibold">How replies start</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          Every agent's composer opens with this. How you greet customers is a house style, so it is a desk
          setting rather than forty personal ones. Signatures stay personal — they carry somebody's own name.
        </p>
        {/* Step 181 — the caret rule in words, not on the canvas. An earlier
            version drew a marker on the blank line itself; it fought the
            editing (Kai couldn't move it or click past it), so it is gone and
            the rule is stated here instead. */}
        <p className="mb-2 flex items-start gap-1.5 text-xs text-muted-foreground">
          <TextCursorInput className="mt-px h-3.5 w-3.5 shrink-0" />
          {startsAtGap ? (
            <span>
              Replies start typing on the first blank line. Put it where you want the cursor to land — press
              Enter to add one, or backspace to remove it.
            </span>
          ) : (
            <span className="text-amber-600 dark:text-amber-500">
              There is no blank line, so replies will start at the very end, below the signature. Press Enter
              where you want agents to begin.
            </span>
          )}
        </p>
        <RichTextEditor
          value={current.replyTemplate || DEFAULT_REPLY_TEMPLATE}
          onChange={(v) => set({ replyTemplate: v === DEFAULT_REPLY_TEMPLATE ? '' : v })}
        />
        <div className="mt-2 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          <p className="mb-1.5 font-medium">You can use:</p>
          <ul className="space-y-0.5">
            {Object.entries(REPLY_TOKENS).map(([token, what]) => (
              <li key={token}>
                <code className="rounded bg-muted px-1">{token}</code> — {what}
              </li>
            ))}
          </ul>
          <p className="mt-2">
            A token with nothing to fill it drops its whole line, so a requester with no name on record gets
            no empty greeting.
          </p>
        </div>
        {current.replyTemplate && (
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => set({ replyTemplate: '' })}>
            Back to the default
          </Button>
        )}
      </section>

      {/* ── Next ticket ID — its own atomic act ───────────────────────── */}
      <NextTicketId current={d.nextTicketId} highest={d.highestTicketNumber} />

      {/* ── Security ──────────────────────────────────────────────────── */}
      <section className="mt-6 rounded-lg border bg-card p-4">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <ShieldCheck className="h-4 w-4" /> Security
        </h2>

        <div className="mt-3">
          <p className="text-sm font-medium">Files people may attach</p>
          <p className="mb-2 text-xs text-muted-foreground">
            Untick to remove a type. Nothing outside this list can be added — the validator, not this page,
            decides the ceiling.
          </p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {d.codeUploadAccept.map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input"
                  checked={allowed.has(t)}
                  onChange={(e) => {
                    const next = new Set(allowed);
                    if (e.target.checked) next.add(t);
                    else next.delete(t);
                    const list = d.codeUploadAccept.filter((x) => next.has(x));
                    // Every type ticked = the code default, stored as null so a
                    // type added to the validator later arrives allowed.
                    setSec({ uploadTypes: list.length === d.codeUploadAccept.length ? null : list });
                  }}
                />
                {TYPE_LABELS[t] ?? t}
              </label>
            ))}
          </div>
          {allowed.size === 0 && (
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
              Nobody can attach anything. That is allowed, but say so to your agents.
            </p>
          )}
        </div>

        <div className="mt-5">
          <p className="text-sm font-medium">Who can sign up or create tickets</p>
          <p className="mb-2 text-xs text-muted-foreground">
            Email domains that may self-serve through the portal and by email. Empty means anyone. Agents can
            always add people from any domain — that is a decision, not self-service.
          </p>
          <TokenSelect
            value={current.security.signupDomains}
            options={current.security.signupDomains.map((x) => ({ value: x, label: x }))}
            onChange={(v) => setSec({ signupDomains: v })}
            allowFree
            placeholder="Type a domain and press Enter, e.g. acme.com"
            ariaLabel="Sign-up domains"
          />
        </div>

        <div className="mt-5 max-w-xs">
          <label className="mb-1 block text-sm font-medium">Force sign-out after</label>
          <Select
            value={String(current.security.sessionTimeoutMinutes ?? '')}
            onChange={(e) =>
              setSec({ sessionTimeoutMinutes: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">Never (the sign-in lasts a working day)</option>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="240">4 hours</option>
            <option value="480">8 hours</option>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">Applies within about 30 seconds of the limit.</p>
        </div>
      </section>

      <div className="sticky bottom-0 mt-4 flex items-center justify-end gap-3 border-t bg-background/95 py-3 backdrop-blur">
        {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        <Button variant="ghost" disabled={!dirty || save.isPending} onClick={() => setDraft(null)}>
          Discard
        </Button>
        <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}

/**
 * The next ticket number — outside the settings draft on purpose.
 *
 * Renumbering is a deliberate act with its own rule (must be above every
 * existing ticket), and the Freshdesk import is the reason it exists: the
 * export measurement says seed at 17009. It saves on its own button, so it
 * cannot be changed by accident while adjusting a date format.
 */
function NextTicketId({ current, highest }: { current: number; highest: number }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(String(current));
  const [error, setError] = useState<string | null>(null);
  const n = Number(value);
  const valid = Number.isInteger(n) && n > highest;
  const changed = n !== current;
  const save = useMutation({
    mutationFn: () => apiSend('/api/admin/service-desk/next-ticket-id', 'PUT', { next: n }),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be set.');
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.serviceDesk });
      toast.success(`The next ticket will be #${n}.`);
    },
  });
  return (
    <section className="mt-6 rounded-lg border bg-card p-4">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold">
        <Hash className="h-4 w-4" /> Next ticket ID
      </h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        The number the next ticket gets. Must be higher than the highest existing ticket, #{highest}. Set this
        above the last Freshdesk number before importing so old references keep resolving.
      </p>
      <div className="mt-3 flex items-end gap-2">
        <div className="w-40">
          <Input
            type="number"
            min={highest + 1}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="font-mono"
          />
        </div>
        <Button size="sm" disabled={!valid || !changed || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Setting…' : 'Set'}
        </Button>
        {!valid && value !== '' && <p className="text-xs text-destructive">Must be above #{highest}.</p>}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    </section>
  );
}
